/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';

// Extend Axios config to support skipping auth redirect on 401
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _skipAuthRedirect?: boolean;
}
import type {
  AuthResponse,
  LoginRequest,
  SignupRequest,
  OTPRequest,
  User,
  UserProfile,
  OnboardingData,
  NutritionTargets,
  Food,
  FoodSearchResult,
  CreateFoodRequest,
  MealEntry,
  AddMealEntryRequest,
  UpdateMealEntryRequest,
  DailyNutritionSummary,
  Recipe,
  RecipeSearchResult,
  CreateRecipeRequest,
  WeightEntry,
  AddWeightEntryRequest,
  WaterIntake,
  AddWaterIntakeRequest,
  BodyMeasurement,
  AddBodyMeasurementRequest,
  HealthMetrics,
  ChatSession,
  SendMessageRequest,
  AssistantResponse,
  ApiError,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

// Token management
const TOKEN_KEY = 'goodfud_token';

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const config = error.config as ExtendedAxiosRequestConfig | undefined;
    if (error.response?.status === 401 && !config?._skipAuthRedirect) {
      // Unauthorized - clear token and redirect to login (full reload to reset app state)
      removeToken();
      window.location.href = '/login';
    }

    const apiError: ApiError = {
      detail: error.response?.data?.detail || error.message || 'An error occurred',
      status_code: error.response?.status,
    };

    return Promise.reject(apiError);
  }
);

// ==================== AUTH API ====================

export const authApi = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    // Skip the global 401→/login redirect so bad credentials show an error on the login form.
    const response = await apiClient.post<AuthResponse>('/auth/login', data, {
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    setToken(response.data.access_token);
    return response.data;
  },

  signup: async (data: SignupRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/register', {
      first_name: data.email.split('@')[0],
      email: data.email,
      password: data.password,
      phone: data.phone_number,
    });
    setToken(response.data.access_token);
    return response.data;
  },

  checkAvailability: async (data: { email?: string; phone?: string }): Promise<{ email_available: boolean; phone_available: boolean }> => {
    const response = await apiClient.post('/auth/check-availability', data, {
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    return response.data;
  },

  verifyOTP: async (data: OTPRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/verify-otp', data);
    setToken(response.data.access_token);
    return response.data;
  },

  resendOTP: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-otp', { email });
    return response.data;
  },

  requestPhoneOTP: async (phone: string): Promise<{ message: string; expires_in: number; channel?: string; dev_otp?: string }> => {
    const response = await apiClient.post('/auth/otp/phone', { phone }, {
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    return response.data;
  },

  loginWithPhoneOTP: async (phone: string, otp: string): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/otp/verify', { phone, otp }, {
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    setToken(response.data.access_token);
    return response.data;
  },

  socialLogin: async (provider: 'google' | 'apple', token: string): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/social', { provider, token }, {
      _skipAuthRedirect: true,
    } as ExtendedAxiosRequestConfig);
    setToken(response.data.access_token);
    return response.data;
  },

  logout: async (): Promise<void> => {
    removeToken();
  },

  getCurrentUser: async (): Promise<User> => {
    // Skip global 401 redirect — caller handles invalid token gracefully
    const response = await apiClient.get<User>('/auth/me', { _skipAuthRedirect: true } as ExtendedAxiosRequestConfig);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },
};

// ==================== USER PROFILE API ====================

export const userProfileApi = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>('/users/profile');
    return response.data;
  },

  createProfile: async (data: OnboardingData): Promise<UserProfile> => {
    // Transform OnboardingData to match backend's expected format
    const nameParts = data.full_name.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || undefined;

    // Calculate date of birth from age
    const currentDate = new Date();
    const birthYear = currentDate.getFullYear() - data.age;
    const dateOfBirth = new Date(birthYear, 0, 1).toISOString().split('T')[0];

    // Map frontend activity levels to backend enum values
    const activityLevelMap: Record<string, string> = {
      'sedentary': 'sedentary',
      'lightly_active': 'light',
      'moderately_active': 'moderate',
      'very_active': 'very_active',
      'extremely_active': 'very_active', // Backend doesn't have extremely_active, map to very_active
    };

    const profileData = {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dateOfBirth,
      gender: data.gender,
      height_cm: data.height,
      current_weight_kg: data.weight,
      target_weight_kg: data.target_weight,
      activity_level: activityLevelMap[data.activity_level] || 'moderate',
      health_goal: data.health_goal,
      dietary_preference: data.dietary_preference || 'non_veg',
      veg_days: data.veg_days || [],
      allergies: data.allergies || [],
      foods_to_avoid: data.foods_to_avoid || [],
    };

    console.log('Sending profile data to backend:', profileData);

    // Use PUT for both create and update since backend treats them the same
    const response = await apiClient.put<UserProfile>('/users/me/profile', profileData);
    return response.data;
  },

  updateProfile: async (data: Partial<OnboardingData>): Promise<UserProfile> => {
    // Transform frontend form field names to backend schema field names
    const activityLevelMap: Record<string, string> = {
      'sedentary': 'sedentary',
      'lightly_active': 'light',
      'moderately_active': 'moderate',
      'very_active': 'very_active',
      'extremely_active': 'very_active',
      'light': 'light',
      'moderate': 'moderate',
      'active': 'moderate',
    };

    const payload: Record<string, unknown> = {};

    if (data.full_name) {
      const parts = data.full_name.trim().split(' ');
      payload.first_name = parts[0];
      if (parts.length > 1) payload.last_name = parts.slice(1).join(' ');
    }
    if (data.age) {
      const birthYear = new Date().getFullYear() - data.age;
      payload.date_of_birth = `${birthYear}-01-01`;
    }
    if (data.gender) payload.gender = data.gender;
    if (data.height) payload.height_cm = data.height;
    if (data.weight) payload.current_weight_kg = data.weight;
    if (data.target_weight) payload.target_weight_kg = data.target_weight;
    if (data.activity_level) payload.activity_level = activityLevelMap[data.activity_level] || data.activity_level;
    if ((data as any).health_goal !== undefined) payload.health_goal = (data as any).health_goal;
    if (data.dietary_preference) payload.dietary_preference = data.dietary_preference; // skip empty string
    if (data.allergies !== undefined) payload.allergies = data.allergies;
    if (data.foods_to_avoid !== undefined) payload.foods_to_avoid = data.foods_to_avoid;
    if (data.veg_days !== undefined) payload.veg_days = data.veg_days;
    if ((data as any).occupation) payload.occupation = (data as any).occupation;

    const response = await apiClient.put<UserProfile>('/users/me/profile', payload);
    return response.data;
  },

  getNutritionTargets: async (): Promise<NutritionTargets> => {
    const response = await apiClient.get<NutritionTargets>('/nutrition/targets');
    return response.data;
  },

  deleteAccount: async (): Promise<void> => {
    await apiClient.delete('/users/me');
  },

  changePhone: async (newPhone: string, otp: string): Promise<{ message: string; phone: string }> => {
    const response = await apiClient.post('/users/me/change-phone', { new_phone: newPhone, otp });
    return response.data;
  },
};

// ==================== FOOD API ====================

export const foodApi = {
  searchFoods: async (query: string, page = 1, pageSize = 20): Promise<FoodSearchResult> => {
    const response = await apiClient.get<FoodSearchResult>('/foods/search', {
      params: { q: query, page, page_size: pageSize },
    });
    return response.data;
  },

  getFoodById: async (id: string): Promise<Food> => {
    const response = await apiClient.get<Food>(`/foods/${id}`);
    return response.data;
  },

  createFood: async (data: CreateFoodRequest): Promise<Food> => {
    const response = await apiClient.post<Food>('/foods', data);
    return response.data;
  },

  scanBarcode: async (barcode: string): Promise<Food> => {
    const response = await apiClient.get<Food>(`/foods/barcode/${barcode}`);
    return response.data;
  },

  getRecentFoods: async (limit = 10): Promise<Food[]> => {
    const response = await apiClient.get<Food[]>('/foods/recent', {
      params: { limit },
    });
    return response.data;
  },
};

// ==================== MEALS API ====================

export const mealsApi = {
  getMealsByDate: async (date: string): Promise<MealEntry[]> => {
    const response = await apiClient.get<MealEntry[]>('/meals', {
      params: { date },
    });
    return response.data;
  },

  addMealEntry: async (data: AddMealEntryRequest): Promise<MealEntry> => {
    const response = await apiClient.post<MealEntry>('/meals', data);
    return response.data;
  },

  updateMealEntry: async (id: string, data: UpdateMealEntryRequest): Promise<MealEntry> => {
    const response = await apiClient.put<MealEntry>(`/meals/${id}`, data);
    return response.data;
  },

  deleteMealEntry: async (id: string): Promise<void> => {
    await apiClient.delete(`/meals/${id}`);
  },

  getDailyNutritionSummary: async (date: string): Promise<DailyNutritionSummary> => {
    const response = await apiClient.get<DailyNutritionSummary>('/meals/daily-summary', {
      params: { date },
    });
    return response.data;
  },

  updateMealLog: async (id: number, data: {
    meal_type?: string;
    calories?: number;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
    notes?: string;
  }) => {
    const response = await apiClient.put(`/meals/log/${id}`, data);
    return response.data;
  },

  deleteMealLog: async (id: number): Promise<void> => {
    await apiClient.delete(`/meals/log/${id}`);
  },
};

// ==================== RECIPES API ====================

export const recipesApi = {
  searchRecipes: async (query: string, page = 1, pageSize = 20): Promise<RecipeSearchResult> => {
    const response = await apiClient.get<RecipeSearchResult>('/recipes/search', {
      params: { q: query, page, page_size: pageSize },
    });
    return response.data;
  },

  getRecipeById: async (id: string): Promise<Recipe> => {
    const response = await apiClient.get<Recipe>(`/recipes/${id}`);
    return response.data;
  },

  createRecipe: async (data: CreateRecipeRequest): Promise<Recipe> => {
    const response = await apiClient.post<Recipe>('/recipes', data);
    return response.data;
  },

  getRecommendedRecipes: async (limit = 10): Promise<Recipe[]> => {
    const response = await apiClient.get<Recipe[]>('/recipes/recommended', {
      params: { limit },
    });
    return response.data;
  },

  addRecipeToMeal: async (recipeId: string, mealType: string, date: string): Promise<MealEntry[]> => {
    const response = await apiClient.post<MealEntry[]>(`/recipes/${recipeId}/add-to-meal`, {
      meal_type: mealType,
      date,
    });
    return response.data;
  },
};

// ==================== HEALTH METRICS API ====================

export const healthApi = {
  getWeightEntries: async (startDate?: string, endDate?: string): Promise<WeightEntry[]> => {
    const response = await apiClient.get<WeightEntry[]>('/health/weight', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  addWeightEntry: async (data: AddWeightEntryRequest): Promise<WeightEntry> => {
    const response = await apiClient.post<WeightEntry>('/health/weight', data);
    return response.data;
  },

  deleteWeightEntry: async (id: string): Promise<void> => {
    await apiClient.delete(`/health/weight/${id}`);
  },

  getStreaks: async (): Promise<any> => {
    const response = await apiClient.get('/health/streaks');
    return response.data;
  },

  getMealStreak: async (): Promise<{
    current_streak: number;
    best_streak: number;
    last_15_days: Array<{ date: string; status: 'delivered' | 'skipped' | 'pending' | 'no_order' }>;
  }> => {
    const response = await apiClient.get('/health/meal-streak');
    return response.data;
  },

  getWaterIntake: async (date: string): Promise<WaterIntake[]> => {
    const response = await apiClient.get<WaterIntake[]>('/health/water', {
      params: { date },
    });
    return response.data;
  },

  addWaterIntake: async (data: AddWaterIntakeRequest): Promise<WaterIntake> => {
    const response = await apiClient.post<WaterIntake>('/health/water', data);
    return response.data;
  },

  deleteWaterIntake: async (id: string): Promise<void> => {
    await apiClient.delete(`/health/water/${id}`);
  },

  getBodyMeasurements: async (): Promise<BodyMeasurement[]> => {
    const response = await apiClient.get<BodyMeasurement[]>('/health/measurements');
    return response.data;
  },

  addBodyMeasurement: async (data: AddBodyMeasurementRequest): Promise<BodyMeasurement> => {
    const response = await apiClient.post<BodyMeasurement>('/health/measurements', data);
    return response.data;
  },

  getHealthMetrics: async (startDate?: string, endDate?: string): Promise<HealthMetrics> => {
    const response = await apiClient.get<HealthMetrics>('/health/metrics', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  getDashboard: async (date: string): Promise<any> => {
    const response = await apiClient.get('/health/dashboard', {
      params: { date },
    });
    return response.data;
  },

  getTrends: async (metricType: string, period: string = '30d'): Promise<any> => {
    const response = await apiClient.get(`/health/trends/${metricType}`, {
      params: { period },
    });
    return response.data;
  },

};

// ==================== AI ASSISTANT API ====================

export const assistantApi = {
  sendMessage: async (data: SendMessageRequest): Promise<AssistantResponse> => {
    const response = await apiClient.post<AssistantResponse>('/assistant/message', data);
    return response.data;
  },

  getChatSessions: async (): Promise<ChatSession[]> => {
    const response = await apiClient.get<ChatSession[]>('/assistant/conversations');
    return response.data;
  },

  getChatSession: async (sessionId: string): Promise<ChatSession> => {
    const response = await apiClient.get<ChatSession>(`/assistant/conversations/${sessionId}`);
    return response.data;
  },

  deleteChatSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/assistant/conversations/${sessionId}`);
  },

  getNutritionAnalysis: async (foodName: string): Promise<any> => {
    const response = await apiClient.post('/assistant/analyze-nutrition', {
      food_name: foodName,
    });
    return response.data;
  },

  getMealSuggestions: async (preferences?: string[]): Promise<any> => {
    const response = await apiClient.post('/assistant/meal-suggestions', {
      preferences,
    });
    return response.data;
  },
};

// ==================== WORKOUT API ====================

export const workoutApi = {
  getPlans: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/workouts/', { params });
    return response.data;
  },

  getFeaturedPlans: async (limit = 3): Promise<any[]> => {
    const response = await apiClient.get(`/workouts/featured?limit=${limit}`);
    return response.data;
  },

  getPlanById: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/workouts/${id}`);
    return response.data;
  },

  createPlan: async (data: any): Promise<any> => {
    const response = await apiClient.post('/workouts/', data);
    return response.data;
  },

  updatePlan: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/workouts/${id}`, data);
    return response.data;
  },

  startPlan: async (data: any): Promise<any> => {
    const response = await apiClient.post('/workouts/assignments', data);
    return response.data;
  },

  getMyAssignments: async (params?: Record<string, any>): Promise<any[]> => {
    const response = await apiClient.get('/workouts/assignments/me', { params });
    return response.data;
  },

  createLog: async (data: any): Promise<any> => {
    const response = await apiClient.post('/workouts/logs', data);
    return response.data;
  },

  getMyLogs: async (params?: Record<string, any>): Promise<any[]> => {
    const response = await apiClient.get('/workouts/logs/me', { params });
    return response.data;
  },

  updateLog: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/workouts/logs/${id}`, data);
    return response.data;
  },

  getAnalyticsStats: async (): Promise<any> => {
    const response = await apiClient.get('/workouts/analytics/stats');
    return response.data;
  },

  getAnalyticsProgress: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/workouts/analytics/progress', { params });
    return response.data;
  },

  getAnalyticsExercises: async (): Promise<any> => {
    const response = await apiClient.get('/workouts/analytics/exercises');
    return response.data;
  },

  getAnalyticsMuscleGroups: async (): Promise<any> => {
    const response = await apiClient.get('/workouts/analytics/muscle-groups');
    return response.data;
  },
};

// ==================== EXERCISE API ====================

export const exerciseApi = {
  getExercises: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/exercises/', { params });
    return response.data;
  },

  getExerciseById: async (id: number | string): Promise<any> => {
    const response = await apiClient.get(`/exercises/${id}`);
    return response.data;
  },

  getBodyParts: async (): Promise<any> => {
    const response = await apiClient.get('/exercises/body-parts');
    return response.data;
  },

  getEquipment: async (): Promise<any> => {
    const response = await apiClient.get('/exercises/equipment');
    return response.data;
  },

  getCategories: async (): Promise<any> => {
    const response = await apiClient.get('/exercises/categories');
    return response.data;
  },

  getFavorites: async (): Promise<any> => {
    const response = await apiClient.get('/exercises/favorites/my');
    return response.data;
  },

  addFavorite: async (exerciseId: number): Promise<any> => {
    const response = await apiClient.post('/exercises/favorites', { exercise_id: exerciseId });
    return response.data;
  },

  removeFavorite: async (exerciseId: number | string): Promise<void> => {
    await apiClient.delete(`/exercises/favorites/${exerciseId}`);
  },
};

// ==================== DIET PLAN API ====================

export const dietPlanApi = {
  getPlans: async (params?: Record<string, any>): Promise<any[]> => {
    const response = await apiClient.get('/diet-plans/', { params });
    return response.data;
  },

  getPlanById: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/diet-plans/${id}`);
    return response.data;
  },

  getRecommendations: async (): Promise<any[]> => {
    const response = await apiClient.get('/diet-plans/recommendations/personalized');
    return response.data;
  },

  subscribe: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-plans/subscribe', data);
    return response.data;
  },

  getActivePlan: async (): Promise<any> => {
    const response = await apiClient.get('/diet-plans/my-plan/active');
    return response.data;
  },

  logMeal: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-plans/log-meal', data);
    return response.data;
  },

  getMealLogs: async (params?: Record<string, any>): Promise<any[]> => {
    const response = await apiClient.get('/diet-plans/meal-logs', { params });
    return response.data;
  },

  getActiveShoppingList: async (): Promise<any> => {
    const response = await apiClient.get('/diet-plans/shopping-list/active');
    return response.data;
  },

  createShoppingList: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-plans/shopping-list', data);
    return response.data;
  },

  markItemsPurchased: async (listId: number, itemIndices: number[]): Promise<any> => {
    const response = await apiClient.put(`/diet-plans/shopping-list/${listId}/mark-purchased`, itemIndices);
    return response.data;
  },

  getAnalytics: async (): Promise<any> => {
    const response = await apiClient.get('/diet-plans/analytics');
    return response.data;
  },

  generatePlan: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-plans/generate', data);
    return response.data;
  },
};

// ==================== DIET TEMPLATES API ====================

export const dietTemplatesApi = {
  getTemplates: async (): Promise<any[]> => {
    const response = await apiClient.get('/diet-templates/templates');
    return response.data;
  },

  generateMealPlan: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-templates/generate', data);
    return response.data;
  },

  generateShoppingList: async (data: any): Promise<any> => {
    const response = await apiClient.post('/diet-templates/shopping-list', data);
    return response.data;
  },
};

// ==================== INGREDIENTS API ====================

export const ingredientsApi = {
  getIngredients: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/ingredients/', { params });
    return response.data;
  },

  getCategories: async (): Promise<any[]> => {
    const response = await apiClient.get('/ingredients/categories');
    return response.data;
  },

  getIngredientById: async (id: number | string): Promise<any> => {
    const response = await apiClient.get(`/ingredients/${id}`);
    return response.data;
  },
};

// ==================== BODY COMPOSITION API ====================

export const bodyCompositionApi = {
  getMyRecords: async (): Promise<any[]> => {
    const response = await apiClient.get('/body-composition/me');
    return response.data;
  },

  getWeekStatus: async (): Promise<{ is_filled: boolean }> => {
    const response = await apiClient.get('/body-composition/this-week-status');
    return response.data;
  },

  submitRecord: async (data: {
    measured_at?: string;
    height_cm?: number;
    weight_kg: number;
    body_fat_percent?: number;
    skeletal_muscle_mass_kg?: number;
    bmr?: number;
    visceral_fat?: number;
    metabolic_age?: number;
    target_weight_kg?: number;
    notes?: string;
  }): Promise<any> => {
    const response = await apiClient.post('/body-composition', data);
    return response.data;
  },
};

// ==================== Referrals API ====================
export const referralsApi = {
  apply: async (referralCode: string): Promise<{ success: boolean; message: string; discount_percent: number }> => {
    const response = await apiClient.post('/referrals/apply', { referral_code: referralCode });
    return response.data;
  },
};

export default apiClient;
